import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from "react";
import {
  notificationService,
  NotificationReadCount,
  Notification,
} from "../services/NotificationService";
import { notificationSocketService } from "../services/NotificationSocketService";
import { useAuth } from "./AuthContext";
import { backendUrl } from "../utils/Environment";

interface NotificationContextType {
  notificationUnreadCount: NotificationReadCount | undefined;
  fetchUnreadCount: () => Promise<void>;
}

const NotificationContext = createContext<NotificationContextType | null>(null);

export const NotificationProvider: React.FC<{ children: ReactNode }> = ({
  children,
}) => {
  const { isLoggedIn } = useAuth();
  const [notificationUnreadCount, setNotificationUnreadCount] = useState<
    NotificationReadCount | undefined
  >();

  const fetchUnreadCount = useCallback(async () => {
    if (!isLoggedIn) return;
    try {
      const { data } = await notificationService.getNotificationUnreadCount();
      setNotificationUnreadCount(data);
      console.log("Fetched unread count:", data);
    } catch (error) {
      console.error("Error fetching unread notification count:", error);
    }
  }, [isLoggedIn]);

  useEffect(() => {
    if (!isLoggedIn) {
      notificationSocketService.disconnect();
      return;
    }

    notificationSocketService.connect(backendUrl);

    notificationSocketService.onNewNotification = (
      notification: Notification
    ) => {
      console.log("🔔 New notification received:", notification);
      setNotificationUnreadCount((prev) => ({
        unreadCount: (prev?.unreadCount ?? 0) + 1,
      }));
    };

    notificationSocketService.onNotificationUpdated = ({
      notificationId,
      updates,
    }) => {
      console.log("🔔 Notification updated:", notificationId, updates);
      if (updates.read === true) {
        setNotificationUnreadCount((prev) => ({
          unreadCount: Math.max(0, (prev?.unreadCount ?? 1) - 1),
        }));
      }
    };

    notificationSocketService.onNotificationDeleted = ({ notificationId }) => {
      console.log("🔔 Notification deleted:", notificationId);
      fetchUnreadCount();
    };

    notificationSocketService.onNotificationsRead = ({ notificationIds }) => {
      console.log("🔔 Notifications read:", notificationIds);
      // If notificationIds is undefined or empty, set unread count to 0 (mark all as read case)
      if (
        !notificationIds ||
        !Array.isArray(notificationIds) ||
        notificationIds.length === 0
      ) {
        setNotificationUnreadCount({ unreadCount: 0 });
      } else {
        setNotificationUnreadCount((prev) => ({
          unreadCount: Math.max(
            0,
            (prev?.unreadCount ?? 0) - notificationIds.length
          ),
        }));
      }
    };

    notificationSocketService.onUnreadCountChanged = (count: number) => {
      console.log("🔔 Unread count changed:", count);
      setNotificationUnreadCount({ unreadCount: count });
    };

    notificationSocketService.onReconnect = () => {
      console.log("🔔 Socket reconnected, refreshing unread count");
      fetchUnreadCount();
    };

    notificationSocketService.onMaxReconnectAttempts = () => {
      console.error("🔔 Max socket reconnection attempts reached");
    };

    return () => {
      notificationSocketService.disconnect();
    };
  }, [isLoggedIn, fetchUnreadCount]);

  useEffect(() => {
    if (isLoggedIn) {
      fetchUnreadCount();
    }
  }, [isLoggedIn, fetchUnreadCount]);

  return (
    <NotificationContext.Provider
      value={{ notificationUnreadCount, fetchUnreadCount }}
    >
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = (): NotificationContextType => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error(
      "useNotifications must be used within a NotificationProvider"
    );
  }
  return context;
};
